import { env } from "@/env";
import { measure, normalizeUrlPath } from "@/metrics";
import { exponentialBackoff, ExponentialBackoffError } from "@/app/_utilities/exponentialBackoff";
import { unstable_cache } from "next/cache";

export interface CacheOptions {
  keys: string[];
  // Tags are mandatory for proper management.
  tags: [string, ...string[]];
  revalidate?: number;
  noCache?: boolean;
  onCacheStatus?: (status: "hit" | "miss") => void;
}

export const DEFAULT_FETCH_RETRY_OPTIONS = {
  maxRetries: 3,
  baseDelay: 250,
  backoffFactor: 4,
} as const;

export interface FetchRetryOptions {
  maxRetries?: number;
  baseDelay?: number;
  backoffFactor?: number;
  shouldRetry?: (error: unknown, attempt: number) => boolean;
  onRetry?: (error: unknown, attempt: number, delay: number) => void;
}

const HTTP_FETCH_ERROR_PATTERN = /^HTTP (\d{3}) fetching /;

export const getHttpStatusFromFetchError = (err: unknown) => {
  if (!(err instanceof Error)) return;
  const match = err.message.match(HTTP_FETCH_ERROR_PATTERN);
  if (!match) return;
  return Number(match[1]);
};

/** Matches errors thrown by `fetchWithCache` — retries 5xx, 429, and network failures. */
export const isRetryableHttpFetchError = (err: unknown): boolean => {
  const status = getHttpStatusFromFetchError(err);
  if (status !== undefined) {
    if (status === 429) return true;
    return status >= 500 && status < 600;
  }
  // fetch() rejects with TypeError on network-level failures.
  return err instanceof TypeError;
};

/**
 * General-purpose `unstable_cache` wrapper with consistent defaults and a `noCache` escape hatch.
 * Use for caching any async function (e.g. wrapping `measure()` + fetch, or higher-level API calls).
 *
 * For raw HTTP calls, prefer `fetchWithCache` which builds on this and auto-sets `cache: "no-store"`.
 */
export async function withCache<T>(fn: () => Promise<T>, options: CacheOptions): Promise<T> {
  if (options.noCache) {
    options.onCacheStatus?.("miss");
    return fn();
  }

  // Mostly for local dev as unstable_cache throws an error if 0 is passed
  const cacheTTL = options.revalidate ?? env.NEXT_PUBLIC_CACHE_PERIOD;
  if (!cacheTTL) {
    options.onCacheStatus?.("miss");
    return fn();
  }

  let called = false;
  const wrapped = async () => {
    called = true;
    return fn();
  };

  const cached = unstable_cache(wrapped, options.keys, {
    tags: options.tags,
    revalidate: cacheTTL,
  });
  try {
    return await cached();
  } finally {
    options.onCacheStatus?.(called ? "miss" : "hit");
  }
}

/**
 * HTTP-specific caching wrapper built on `withCache`.
 * Enforces `cache: "no-store"` to prevent double-caching, checks `response.ok`,
 * and returns parsed JSON. Records metrics via `measure()` — URL is parsed upfront
 * so `api` and `endpoint` labels are correct on both cache hits and misses. Adds a
 * `cache: "hit" | "miss"` label and a `status: "success" | "error"` label.
 */
export async function fetchWithCache<T>(
  url: string,
  init: Omit<RequestInit, "cache" | "next">,
  options: CacheOptions,
): Promise<T> {
  const urlObj = new URL(url);
  const api = urlObj.hostname;
  const endpoint = normalizeUrlPath(urlObj.pathname).replace(/^\//, "");

  let status: "success" | "error" = "success";
  let cache: "hit" | "miss" = "miss";

  return measure(
    "externalApi",
    () => ({ api, endpoint, status, cache }),
    () =>
      withCache(
        async () => {
          try {
            const response = await fetch(url, { ...init, cache: "no-store" });
            if (!response.ok) {
              throw new Error(`HTTP ${response.status} fetching ${url}`);
            }

            return (await response.json()) as T;
          } catch (e) {
            status = "error";
            throw e;
          }
        },
        {
          ...options,
          onCacheStatus: s => {
            cache = s;
            options.onCacheStatus?.(s);
          },
        },
      ),
  );
}

/**
 * `fetchWithCache` with exponential backoff on transient failures.
 * Retries happen outside the cache boundary so failed responses are not memoized.
 */
export async function fetchWithRetryAndCache<T>(
  url: string,
  init: Omit<RequestInit, "cache" | "next">,
  options: CacheOptions,
  retryOptions?: FetchRetryOptions,
): Promise<T> {
  const { maxRetries, baseDelay, backoffFactor, shouldRetry, onRetry } = {
    ...DEFAULT_FETCH_RETRY_OPTIONS,
    ...retryOptions,
  };

  try {
    return await exponentialBackoff(() => fetchWithCache<T>(url, init, options), {
      maxRetries,
      baseDelay,
      backoffFactor,
      shouldRetry: shouldRetry ?? isRetryableHttpFetchError,
      onRetry,
    });
  } catch (err) {
    if (err instanceof ExponentialBackoffError) throw err.lastError;
    throw err;
  }
}
